import type { ProductionPlanDetails, ProductionPlanException, ProductionPlanItem } from './types';

const DAY = 86_400_000;

/** Strict calendar dates; UTC arithmetic avoids DST and browser-zone differences. */
export function productionDateOrdinal(value: string): number {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('INVALID_DATE');
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) throw new Error('INVALID_DATE');
  return Math.floor(parsed.getTime() / DAY);
}

export function productionPlanDetails(plan: ProductionPlanDetails): ProductionPlanDetails {
  return {
    departmentId: plan.departmentId, name: plan.name, dayOfWeek: plan.dayOfWeek,
    targetQuantity: plan.targetQuantity, unit: plan.unit,
    instructions: plan.instructions || '', recipeId: plan.recipeId || null,
    ...(plan.scheduleDate ? { scheduleDate: plan.scheduleDate } : {}),
    ...(plan.recurrence ? { recurrence: plan.recurrence } : {}),
    laborAssignments: plan.laborAssignments || [],
  };
}

export function productionExceptionOnDate(plan: ProductionPlanItem, date: string): ProductionPlanException | undefined {
  productionDateOrdinal(date);
  return [...(plan.exceptions || [])].reverse().find((entry) => entry.date === date);
}

/** Later edits supersede earlier future versions, without touching their history. */
export function productionPlanSnapshotOnDate(plan: ProductionPlanItem, date: string): ProductionPlanItem {
  productionDateOrdinal(date);
  const effective = [...(plan.versions || [])].reverse().find((entry) => entry.effectiveFrom <= date);
  const exception = productionExceptionOnDate(plan, date);
  const details = exception?.status !== 'INHERIT' && exception?.details ? exception.details : effective?.details;
  return details ? { ...plan, ...details } : plan;
}

export function productionRuleScheduledOn(plan: ProductionPlanDetails, date: string): boolean {
  const day = productionDateOrdinal(date), weekday = ((day + 4) % 7 + 7) % 7;
  if (!plan.recurrence) return plan.dayOfWeek === weekday;
  const rule = plan.recurrence;
  const difference = day - productionDateOrdinal(rule.startsOn);
  if (difference < 0 || (!rule.indefinite && rule.endsOn && date > rule.endsOn)) return false;
  if (rule.frequency === 'NONE') return difference === 0;
  if (rule.frequency === 'DAILY') return difference % rule.interval === 0;
  return Math.floor(difference / 7) % rule.interval === 0 && (rule.daysOfWeek || [plan.dayOfWeek]).includes(weekday);
}

/** Resolve the exact editable/completable occurrence while keeping its stable plan ID. */
export function productionPlanOnDate(plan: ProductionPlanItem, date: string): ProductionPlanItem | null {
  productionDateOrdinal(date);
  if (!plan.active || (plan.endedFrom && date >= plan.endedFrom)) return null;
  const exception = productionExceptionOnDate(plan, date);
  if (exception?.status === 'CANCELLED') return null;
  const snapshot = productionPlanSnapshotOnDate(plan, date);
  if (exception?.status === 'EDITED') return snapshot;
  return productionRuleScheduledOn(snapshot, date) ? snapshot : null;
}

export const productionScheduledOn = (plan: ProductionPlanItem, date: string): boolean => productionPlanOnDate(plan, date) !== null;

/** With no date, every retained snapshot is a reference; dated queries mean live future work. */
export function productionRecipeReferenced(plan: ProductionPlanItem, recipeId: string, fromDate?: string): boolean {
  const snapshots = [plan, ...(plan.versions || []).map((entry) => entry.details), ...(plan.exceptions || []).flatMap((entry) => entry.details ? [entry.details] : [])];
  if (!snapshots.some((entry) => entry.recipeId === recipeId)) return false;
  if (!fromDate) return true;
  if (!plan.active || (plan.endedFrom && plan.endedFrom <= fromDate)) return false;
  const startDay = productionDateOrdinal(fromDate), maxDay = productionDateOrdinal('9999-12-31');
  const boundaries = [...new Set([startDay, ...(plan.versions || []).map((entry) => productionDateOrdinal(entry.effectiveFrom)).filter((day) => day > startDay)])].sort((a, b) => a - b);
  const overallEnd = plan.endedFrom ? productionDateOrdinal(plan.endedFrom) - 1 : maxDay;
  const iso = (day: number) => new Date(day * DAY).toISOString().slice(0, 10);
  for (const exception of plan.exceptions || []) {
    if (exception.date >= fromDate && productionPlanOnDate(plan, exception.date)?.recipeId === recipeId) return true;
  }
  for (let index = 0; index < boundaries.length; index++) {
    const from = boundaries[index], to = Math.min(overallEnd, (boundaries[index + 1] ?? maxDay + 1) - 1);
    if (from > to) continue;
    const details = [...(plan.versions || [])].reverse().find((entry) => productionDateOrdinal(entry.effectiveFrom) <= from)?.details || plan;
    if (details.recipeId !== recipeId) continue;
    const first = Math.max(from, details.recurrence ? productionDateOrdinal(details.recurrence.startsOn) : from);
    const last = Math.min(to, details.recurrence && !details.recurrence.indefinite && details.recurrence.endsOn ? productionDateOrdinal(details.recurrence.endsOn) : to,
      first + 364 * ((plan.exceptions || []).length + 1));
    for (let day = first; day <= last; day++) if (productionPlanOnDate(plan, iso(day))?.recipeId === recipeId) return true;
  }
  return false;
}
